<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('Required information for MobilePay activation') }}</h4>
    </div>
    <div class="modal-body">
      <editor-tabs id="tabNavi" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-md-12">
                <div class="well text-info">
                  <i class="fa fa-fw fa-info-circle"></i> {{ _('Please fill in all fields before sending the application.') }}
                </div>
                <div class="form-group">
                  <label for="country">{{ _('Country') }}</label>
                  <select id="country" v-model="application.country" class="form-control">
                    <option value="FI">{{ _('Finland') }}</option>
                    <option value="NO">{{ _('Norway') }}</option>
                    <option value="SV">{{ _('Sweden') }}</option>
                    <option value="DK">{{ _('Denmark') }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="turnover">{{ _('Annual turnover') }}</label>
                  <div class="input-group">
                    <input type="number" class="form-control" id="turnover" :placeholder="_('Enter your annual turnover')" v-model="application.annualTurnover" />
                    <div class="input-group-addon">&euro;</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="purpose">{{ _('Intended purpose') }}</label>
                  <select id="purposeSelector" v-model="application.selectedPurpose" class="form-control">
                    <option value="none" v-if="!preselectedPurpose">{{ _('Not selected') }}</option>
                    <option value="online" v-if="!preselectedPurpose || preselectedPurpose === 'online'">{{ _('Storefront only') }}</option>
                    <option value="pos" v-if="!preselectedPurpose || preselectedPurpose === 'pos'">{{ _('POS only') }}</option>
                    <option value="both" v-if="!preselectedPurpose || preselectedPurpose === 'both'">{{ _('Storefront and POS') }}</option>
                  </select>
                  <br />
                  <textarea class="form-control" id="purpose" v-model="application.intendedPurpose" rows="5" wrap="virtual"></textarea>
                  <p>{{ _('Please describe the products available in the shop and if they will be sold through the storefront or the point of sale system - or both.') }}</p>
                </div>
              </div>
            </div>
            <div class="row" v-if="showBankAccountId">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="bankaccount">{{ _('Settlement bank account ID (BBAN)') }}</label>
                  <input type="text" class="form-control" id="bankaccount" :placeholder="_('Enter the settlement bank account ID')" v-model="application.bankAccountId" />
                </div>
              </div>
            </div>
            <div class="form-group" v-if="pricePackages.length > 0 && (application.selectedPurpose === 'online' || application.selectedPurpose === 'both')">
              <label for="price-package-online">{{ _('Pricing package') }} ({{  _('Storefront') }})</label>
              <select id="price-package-online" v-model="application.onlinePricePackageId" class="form-control">
                <option v-for="pricePackage in pricePackages.filter(pp => pp.market === application.country && pp.productType === 'PAYMENT_INTEGRATION')" :value="pricePackage.pricePackageId">{{ pricePackage.name }} ({{ pricePackage.description }}) {{ pricePackage.market }}</option>
              </select>
            </div>
            <div class="form-group" v-if="pricePackages.length > 0 && (application.selectedPurpose === 'pos' || application.selectedPurpose === 'both')">
              <label for="price-package-pos">{{ _('Pricing package') }} ({{  _('POS') }})</label>
              <select id="price-package-pos" v-model="application.posPricePackageId" class="form-control">
                <option v-for="pricePackage in pricePackages.filter(pp => pp.market === application.country && pp.productType === 'CASHIER')" :value="pricePackage.pricePackageId">{{ pricePackage.name }} ({{ pricePackage.description }}) {{ pricePackage.market }}</option>
              </select>
            </div>
          </div>
          <div v-bind="tabPanel('compliance1')" class="tab-pane active" id="compliance1" v-if="showComplianceTabs && isTabActive('compliance1')" key="compliance1">
            <div class="row">
              <div class="col-md-12">
                <h4>{{ _('Gift cards') }}</h4>
                <div class="form-group">
                  <label for="giftcard-percoption">{{ _('Percentage of sales that consist of gift cards') }}</label>
                  <select id="giftcard-percoption" v-model="application.giftCard.isLessThanTen" class="form-control">
                    <option value="0">{{ _('Less than 10%') }}</option>
                    <option value="1">{{ _('10% or more') }}</option>
                  </select>
                </div>
                <template v-if="application.giftCard.isLessThanTen == 1">
                  <div class="form-group">
                    <label for="giftcard-validity">{{ _('Gift card validity duration') }}</label>
                    <input type="number" class="form-control" id="giftcard-validity" :placeholder="_('Enter the validity duration in days')" v-model="application.giftCard.validityDuration" />
                  </div>
                  <div class="form-group">
                    <label for="giftcard-perc">{{ _('Percentage of sales that consist of gift cards') }}</label>
                    <input type="number" class="form-control" id="giftcard-perc" :placeholder="_('Enter the percentage of sales that consist of gift cards')" v-model="application.giftCard.turnoverShare" />
                  </div>
                </template>
                <h4>{{ _('Memberships') }}</h4>
                <div class="form-group">
                  <label for="merbership-perc">{{ _('Percentage of sales that are memberships') }}</label>
                  <div class="input-group">
                    <input type="number" class="form-control" id="merbership-perc" :placeholder="_('Enter the estimated turnover share of membership sales in percentage')" v-model="application.membership.turnoverShare" />
                    <div class="input-group-addon">%</div>
                  </div>
                </div>
                <div class="form-group">
                  <label for="membership-validity">{{ _('Membership validity duration') }}</label>
                  <select id="membership-validity" v-model="application.membership.validity" class="form-control">
                    <option value="CurrentCalendarYear">{{ _('Current calendar year') }}</option>
                    <option value="OneYearFromPayment">{{ _('One year from payment') }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="membership-period-distribution">{{ _('Distribution of validity periods') }}</label>
                  <input type="text" class="form-control" id="membership-period-distribution" :placeholder="_('For example 30% yearly 70% monthly')" v-model="application.membership.periodDistribution" />
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('compliance2')" class="tab-pane active" id="compliance2" v-if="showComplianceTabs && isTabActive('compliance2')" key="compliance2">
            <div class="row">
              <div class="col-md-12">
                <h4>{{ _('Subscriptions') }}</h4>
                <div class="form-group">
                  <label for="subscription-perc">{{ _('Percentage of sales that are subscriptions') }}</label>
                  <div class="input-group">
                    <input type="number" class="form-control" id="subscription-perc" :placeholder="_('Enter the estimated turnover share of subscription sales in percentage')" v-model="application.subscription.turnoverShare" />
                    <div class="input-group-addon">%</div>
                  </div>
                  <div class="form-group">
                    <label for="subscription-period-distribution">{{ _('Distribution of validity periods') }}</label>
                    <input type="text" class="form-control" id="subscription-period-distribution" :placeholder="_('For example 30% yearly 70% monthly')" v-model="application.subscription.periodDistribution" />
                  </div>
                </div>
                <h4>{{ _('Courses') }}</h4>
                <div class="form-group">
                  <label for="course-perc">{{ _('Percentage of sales that are courses') }}</label>
                  <div class="input-group">
                    <input type="number" class="form-control" id="course-perc" :placeholder="_('Enter the estimated turnover share of course sales in percentage')" v-model="application.course.turnoverShare" />
                    <div class="input-group-addon">%</div>
                  </div>
                </div>
                <div class="form-group">
                  <label for="course-time-before-order">{{ _('Time from order to course') }}</label>
                  <input type="text" class="form-control" id="course-time-before-order" :placeholder="_('How long in advance is it common to order and pay for a course')" v-model="application.course.timeBeforeOrder" />
                </div>
                <div class="form-group">
                  <label for="course-period">{{ _('Course duration') }}</label>
                  <input type="text" class="form-control" id="course-period" :placeholder="_('How long the duration of a course typically is')" v-model="application.course.period" />
                </div>
                <div class="form-group">
                  <label for="course-online">{{ _('Online course availability') }}</label>
                  <select id="course-online" v-model="application.course.isOnline" class="form-control">
                    <option value="0">{{ _('No online courses are offered') }}</option>
                    <option value="1">{{ _('Yes, online courses are offered') }}</option>
                  </select>
                </div>
                <div class="form-group" v-if="application.course.isOnline == 1">
                  <label for="course-online-accessible-time">{{ _('Online course accessible time') }}</label>
                  <input type="text" class="form-control" id="course-online-accessible-time" :placeholder="_('How long after purchase does a user have access to the course')" v-model="application.course.accessibleTime" />
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('compliance3')" class="tab-pane active" id="compliance3" v-if="showComplianceTabs && isTabActive('compliance3')" key="compliance3">
            <div class="row">
              <div class="col-md-12">
                <h4>{{ _('Ticket sales') }}</h4>
                <div class="form-group">
                  <label for="ticket-perc">{{ _('Percentage of sales that are tickets') }}</label>
                  <div class="input-group">
                    <input type="number" class="form-control" id="ticket-perc" :placeholder="_('Enter the estimated turnover share of ticket sales in percentage')" v-model="application.ticket.turnoverShare" />
                    <div class="input-group-addon">%</div>
                  </div>
                  <div class="form-group">
                    <label for="ticket-prepurchase-time">{{ _('Time from order to ticket usage') }}</label>
                    <input type="text" class="form-control" id="ticket-prepurchase-time" :placeholder="_('How long in advance of an event is it common to order and pay for a ticket')" v-model="application.ticket.prepurchaseTime" />
                  </div>
                </div>
                <h4>{{ _('Rentals') }}</h4>
                <div class="form-group">
                  <label for="rent-perc">{{ _('Percentage of sales that are rentals') }}</label>
                  <div class="input-group">
                    <input type="number" class="form-control" id="rent-perc" :placeholder="_('Enter the estimated turnover share of rental sales in percentage')" v-model="application.rent.turnoverShare" />
                    <div class="input-group-addon">%</div>
                  </div>
                </div>
                <div class="form-group">
                  <label for="rent-prepurchase-time">{{ _('Time from order to rental period') }}</label>
                  <input type="text" class="form-control" id="rent-prepurchase-time" :placeholder="_('How long in advance of the rental period is it common to order and pay')" v-model="application.rent.prepurchaseTime" />
                </div>
                <div class="form-group">
                  <label for="rental-duration">{{ _('Rental duration') }}</label>
                  <input type="text" class="form-control" id="rental-duration" :placeholder="_('How long is the average rental period')" v-model="application.rent.averageDuration" />
                </div>
                <h4>{{ _('Prepaid services') }}</h4>
                <div class="form-group">
                  <label for="prepaid-perc">{{ _('Percentage of sales that are prepaid services') }}</label>
                  <div class="input-group">
                    <input type="number" class="form-control" id="prepaid-perc" :placeholder="_('Enter the estimated turnover share of prepaid service sales in percentage')" v-model="application.prepaidServices.turnoverShare" />
                    <div class="input-group-addon">%</div>
                  </div>
                </div>
                <div class="form-group">
                  <label for="prepaid-prepurchase-time">{{ _('Time from order to prepaid service usage') }}</label>
                  <input type="text" class="form-control" id="prepaid-prepurchase-time" :placeholder="_('How long in advance of the prepaid service usage is it common to order and pay')" v-model="application.prepaidServices.prepurchaseTime" />
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
      <button class="btn btn-success" v-on:click="save" :disabled="!canSave"><i class="fa fa-envelope-open-text fa-fw"></i> {{ _('Send') }}</button>
    </div>
  </div>
</template>

<script>
import errors from '~/lib/errors'
import tabs from '~/lib/tabs'

export default {
  mixins: [errors, tabs],
  props: ['pricePackages', 'baseDomain', 'shopId', 'apiKey', 'settings', 'preselectedPurpose'],
  data () {
    return {
      application: {
        onlinePricePackageId: null,
        posPricePackageId: null,
        country: 'FI',
        giftCard: {},
        membership: {},
        subscription: {},
        course: {},
        ticket: {},
        rent: {},
        prepaidServices: {},
        selectedPurpose: this.preselectedPurpose || 'none',
        merchantCategoryCode: null,
      },
      showBankAccountId: false,
      showComplianceTabs: false
    }
  },
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'compliance1', label: this._('Compliance / 1'), hidden: !this.showComplianceTabs },
        { id: 'compliance2', label: this._('Compliance / 2'), hidden: !this.showComplianceTabs },
        { id: 'compliance3', label: this._('Compliance / 3'), hidden: !this.showComplianceTabs }
      ])
    },
    canSave () {
      if (this.application.selectedPurpose === 'none') {
        return false
      }

      // Pricing package validation
      if (this.pricePackages.length > 0) {
        if (this.application.selectedPurpose === 'online' && !this.application.onlinePricePackageId) {
          return false
        }
        if (this.application.selectedPurpose === 'pos' && !this.application.posPricePackageId) {
          return false
        }
        if (this.application.selectedPurpose === 'both' && !this.application.onlinePricePackageId && !this.application.posPricePackageId) {
          return false
        }
      }

      if (!this.application.country) {
        return false
      }

      // General tab validation
      if (!this.application.annualTurnover || 
          !this.application.intendedPurpose) {
        return false
      }

      // Bank account ID validation
      if (this.showBankAccountId && !this.application.bankAccountId) {
        return false
      }

      if (this.showComplianceTabs) {
        // Gift card validation
        if (this.application.giftCard.isLessThanTen === '1' && 
            (!this.application.giftCard.validityDuration || 
            !this.application.giftCard.turnoverShare)) {
          return false
        }

        // Membership validation
        if (!this.application.membership.turnoverShare ||
            !this.application.membership.validity ||
            !this.application.membership.periodDistribution) {
          return false
        }

        // Subscription validation
        if (!this.application.subscription.turnoverShare ||
            !this.application.subscription.periodDistribution) {
          return false
        }

        // Course validation
        if (!this.application.course.turnoverShare ||
            !this.application.course.timeBeforeOrder ||
            !this.application.course.period ||
            !this.application.course.isOnline ||
            (this.application.course.isOnline === '1' && !this.application.course.accessibleTime)) {
          return false
        }

        // Ticket validation
        if (!this.application.ticket.turnoverShare ||
            !this.application.ticket.prepurchaseTime) {
          return false
        }

        // Rental validation
        if (!this.application.rent.turnoverShare ||
            !this.application.rent.prepurchaseTime ||
            !this.application.rent.averageDuration) {
          return false
        }

        // Prepaid services validation
        if (!this.application.prepaidServices.turnoverShare ||
            !this.application.prepaidServices.prepurchaseTime) {
          return false
        }
      }

      return true
    },
  },
  methods: {
    hideModal () {
      this.$emit('close')
    },
    save () {
      // Create a copy of the application data
      let applicationData = JSON.parse(JSON.stringify(this.application))

      // Remove bank account if not enabled
      if (!this.showBankAccountId) {
        delete applicationData.bankAccountId
      }

      // Remove compliance fields if not enabled
      if (!this.showComplianceTabs) {
        delete applicationData.giftCard
        delete applicationData.membership
        delete applicationData.subscription
        delete applicationData.course
        delete applicationData.ticket
        delete applicationData.rent
        delete applicationData.prepaidServices
      }
      this.$emit('done', applicationData)
    }
  },
  created() {
  }
}
</script>